import { forwardRef, useEffect, useMemo } from 'react';
import { css, cx } from '@linaria/atomic';
import composeRefs from '@seznam/compose-react-refs';
import { Icon } from '@frontend/icons';
import { SpinningLoader } from '../loader';
import { useTooltip } from '../tooltip';
import { ButtonProps } from './button.type';
import { getIconColor, getLoaderColor, getVariantClassName } from './styles';
import { buttonContentBase, getButtonContentClassName } from './styles/common.styles';

const eventsToDisable = ['onClick', 'onMouseDown', 'onMouseUp', 'onKeyDown', 'onKeyUp'];

const LOADER_CLASS = css`
  margin: 0;
`;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      destructive = false,
      disabled = false,
      hoverLabel = '',
      hoverLabelPlacement = 'top',
      iconName,
      loading = false,
      showLabelWhenDisabled = false,
      size = 'small',
      trackingId,
      type = 'button',
      variant = 'primary',
      label,
      showFocusOutline,
      className,
      ...rest
    },
    ref
  ) => {
    const iconOnly = !children && !!iconName;

    const buttonClassName = useMemo(() => {
      // getVariantClassName() has a few method calls. Better to memoize if props aren't changing.
      return getVariantClassName({ variant, size, loading, iconOnly, destructive, showFocusOutline });
    }, [variant, size, loading, iconOnly, destructive, showFocusOutline]);

    const effectivelyDisabled = disabled || loading;

    const eventHandlers = eventsToDisable.reduce((handlers, eventName) => {
      handlers[eventName] = (event) => {
        if (effectivelyDisabled) {
          event.preventDefault();
          return;
        }
        rest[eventName]?.(event);
      };
      return handlers;
    }, {});

    const { Tooltip, tooltipProps, triggerProps, isOpen, setOpen } = useTooltip({
      placement: hoverLabelPlacement,
    });
    // if hover label is visible when button is disabled
    // it can hang around until the button is reenabled
    useEffect(() => {
      if (disabled && !showLabelWhenDisabled && isOpen) {
        setOpen(false);
      }
    }, [disabled, isOpen]);

    return (
      <button
        aria-disabled={effectivelyDisabled}
        className={cx(buttonClassName, className)}
        data-trackingid={trackingId}
        ref={composeRefs(ref, triggerProps.ref) as React.Ref<HTMLButtonElement>}
        tabIndex={effectivelyDisabled ? -1 : 0}
        type={type}
        aria-label={label}
        data-fabric-ds-name='Button'
        {...rest}
        {...eventHandlers}
      >
        {loading ? (
          <SpinningLoader
            color={getLoaderColor(variant, destructive)}
            className={LOADER_CLASS}
            size={size === 'tiny' ? 'xxs' : 'xs'}
          />
        ) : (
          <>
            {!!hoverLabel && <Tooltip {...tooltipProps}>{hoverLabel}</Tooltip>}
            <span className={cx(buttonContentBase, getButtonContentClassName(size))}>
              {iconName && (
                <Icon
                  name={iconName}
                  className='button-icon'
                  color={getIconColor(variant, disabled, destructive)}
                  size={size === 'large' || size === 'medium' ? 24 : size === 'tiny' ? 14 : 16}
                />
              )}
              {children}
            </span>
          </>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';
